import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Menu } from 'lego-on-react';
import * as React from 'react';
import { MouseEvent } from 'react';

import { Shape2, Shape2Icon, Shape2Theme } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { getActiveDevelopmentTools } from '../../service/DevHelpers/DevHelpers';
import { TestBem } from '../../service/TestBem';
import { ThemePopup } from '../../typings/common';
import { DevelopmentToolsActionType, DevInfo, FakeAuthData } from '../../typings/dev';
import { DevInfoControl } from '../DevInfoControl/DevInfoControl';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';

import './DevelopmentTools.css';
import * as keyset from './DevelopmentTools.i18n';
import { FakeAuthModal } from './FakeAuthModal/FakeAuthModal';
import { FeaturesModal } from './FeaturesModal/FeaturesModal';

const i18n = i18nFactory(keyset);

export interface DevelopmentToolsProps {
    devInfo: DevInfo;
    switchShowCaptchaState: () => void;
    switchDumpVarsState: () => void;
    doFakeAuth: (login: string) => void;
    fakeAuth?: FakeAuthData;
}

export interface DevelopmentToolsState {
    isPopupOpen: boolean;
    isModalOpen: boolean;
    isFeaturesModalOpen: boolean;
}

export class DevelopmentTools extends React.Component<DevelopmentToolsProps, DevelopmentToolsState> {
    constructor(props: DevelopmentToolsProps) {
        super(props);

        this.state = {
            isPopupOpen: false,
            isModalOpen: false,
            isFeaturesModalOpen: false
        };
    }

    render(): React.ReactNode {
        const { isPopupOpen, isModalOpen, isFeaturesModalOpen } = this.state;
        const { devInfo, doFakeAuth, fakeAuth } = this.props;
        const {
            betaNumber,
            clientId,
            role,
            dbInfo: { shard, configuration }
        } = devInfo;

        return (
            <>
                <div
                    className={classNames(Bem.cls('development-tools'), TestBem.cls('development-tools'))}
                    onClick={this.switchPopupState}
                >
                    <Shape2
                        cls={Bem.cls('development-tools', 'shape')}
                        width={16}
                        height={16}
                        theme={Shape2Theme.light}
                        hoverable={true}
                        icon={Shape2Icon.magicWand}
                    />
                </div>
                <PopupDecorator
                    visible={isPopupOpen}
                    anchor={this}
                    theme={ThemePopup.CLEAR}
                    target="anchor"
                    onOutsideClick={this.closePopup}
                    hasTail={true}
                >
                    <Menu
                        type={'check'}
                        theme={'normal'}
                        size={'s'}
                        items={this.getDevelopmentTools()}
                        val={getActiveDevelopmentTools()}
                        onClick={this.onMenuItemSelect}
                    />
                    <div className={Bem.cls('development-tools', 'dev-info')}>
                        {betaNumber && (
                            <div>
                                {i18n('beta-number')}: {betaNumber}
                            </div>
                        )}
                        {configuration && (
                            <div>
                                {i18n('bd-config')}: {configuration}
                            </div>
                        )}
                        {shard && (
                            <div>
                                {i18n('shard')}: {shard}
                            </div>
                        )}
                        {role && (
                            <div>
                                {i18n('role')}: {role}
                            </div>
                        )}
                        {fakeAuth && (
                            <>
                                <div>
                                    {i18n('auth-as')}: {fakeAuth.fakeName}
                                </div>
                                <div>uid: {fakeAuth.uid}</div>
                            </>
                        )}
                        {clientId && <div>clientId: {clientId}</div>}
                        <DevInfoControl cls={Bem.cls('development-tools', 'svn-info')} devInfo={devInfo} />
                    </div>
                </PopupDecorator>
                <FakeAuthModal onAccept={doFakeAuth} isVisible={isModalOpen} onClose={this.closeModal} />
                <FeaturesModal
                    isVisible={isFeaturesModalOpen}
                    onClose={this.closeFeaturesModal}
                    operatorFeatures={devInfo.operatorFeatures}
                    clientFeatures={devInfo.clientFeatures}
                />
            </>
        );
    }

    private switchPopupState = (): void => this.setState({ isPopupOpen: !this.state.isPopupOpen });
    private closePopup = (): void => this.setState({ isPopupOpen: false });

    private getDevelopmentTools = (): Array<object> => {
        return [
            {
                text: i18n('fake-auth'),
                val: DevelopmentToolsActionType.fakeAuth
            },
            {
                text: i18n('disable-captcha'),
                val: DevelopmentToolsActionType.showCaptcha
            },
            {
                text: i18n('dump-vars'),
                val: DevelopmentToolsActionType.dumpVars
            },
            {
                text: i18n('features'),
                val: DevelopmentToolsActionType.features
            }
        ];
    };

    private onMenuItemSelect = (event: MouseEvent<HTMLElement>, value: DevelopmentToolsActionType): void => {
        const { switchShowCaptchaState, switchDumpVarsState } = this.props;

        switch (value) {
            case DevelopmentToolsActionType.dumpVars:
                switchDumpVarsState();
                break;
            case DevelopmentToolsActionType.showCaptcha:
                switchShowCaptchaState();
                break;
            case DevelopmentToolsActionType.fakeAuth:
                this.openModal();
                break;
            case DevelopmentToolsActionType.features:
                this.openFeaturesModal();
                break;
            default:
                throw new Error(`Unknown dev tool - ${value}`);
        }
    };

    private openFeaturesModal = (): void => this.setState({ isFeaturesModalOpen: true });
    private closeFeaturesModal = (): void => this.setState({ isFeaturesModalOpen: false });
    private openModal = (): void => this.setState({ isModalOpen: true });
    private closeModal = (): void => this.setState({ isModalOpen: false });
}
